// Athlete Baseline Assessment — full form schema.
// Used by BaselineFormModal (data entry) and MedicalHistoryReport (print).
// Field types: text, number, date, textarea, select, multi, checkbox

export const BASELINE_SCHEMA = [
  {
    id: "id",
    section: "Identification",
    fields: [
      { key: "assessment_date", label: "Assessment Date", type: "date" },
      { key: "dob",             label: "Date of Birth", type: "date" },
      { key: "age",             label: "Age", type: "computed", from: "dob", unit: "yrs" },
      { key: "gender",          label: "Gender", type: "select", options: ["Male", "Female", "Other"] },
      // Blood group is a dropdown driven by the `/blood-groups` master
      // so admins can add rare phenotypes (Bombay Oh, Rh-null) without
      // a code deploy. The BaselineFormModal fetches options at mount.
      { key: "blood_group",     label: "Blood group", type: "blood_group" },
      { key: "school",          label: "School / Academy", type: "school" },
      { key: "grade",           label: "Grade", type: "text" },
      { key: "gov_id",          label: "Gov ID / Aadhaar", type: "text" },
      { key: "passport",        label: "Passport Number", type: "text" },
      { key: "address",         label: "Address", type: "textarea", full: true },
      { key: "parent_guardian", label: "Parent / Guardian", type: "text" },
      { key: "relationship",    label: "Relationship", type: "text" },
      { key: "mobile",          label: "Athlete Mobile", type: "text" },
      { key: "athlete_email",   label: "Athlete Email", type: "text" },
      { key: "guardian_mobile", label: "Guardian Mobile", type: "text" },
      { key: "guardian_email",  label: "Guardian Email", type: "text" },
      { key: "emergency_contact", label: "Emergency Contact", type: "text" },
    ],
  },
  {
    id: "phys",
    section: "Physical Metrics & Growth",
    // Height & Weight intentionally omitted — captured in the
    // Biometrics tab (which stores a time-series). Duplicating them
    // here was confusing coaches; BMI stays because it's derived and
    // rarely re-entered. (2026-02-20 per user request.)
    fields: [
      { key: "bmi",        label: "BMI",    type: "number" },
      { key: "growth",     label: "Growth", type: "select", options: ["Underweight", "Normal", "Overweight"] },
      { key: "pulse_bpm",  label: "Pulse",  type: "number", unit: "bpm" },
      { key: "bp",         label: "Blood Pressure", type: "text", placeholder: "e.g. 120/80" },
      { key: "rr",         label: "Respiratory Rate", type: "number" },
      { key: "hearing",    label: "Hearing", type: "select", options: ["Normal", "Delayed"] },
      { key: "dental",     label: "Dental",  type: "select", options: ["Normal", "Caries"] },
      { key: "anemia",     label: "Anemia (Pallor)", type: "select", options: ["No", "Yes"] },
      { key: "skin_hair",  label: "Skin / Hair (Nutritional Signs)", type: "textarea", full: true },
      { key: "general_exam", label: "General Exam Findings", type: "textarea", full: true },
    ],
  },
  {
    id: "med",
    section: "Medical History & Systems Review",
    fields: [
      { key: "hospitalization", label: "Hospitalization / Surgery / Fractures", type: "textarea", full: true,
        placeholder: "Enter reasons and dates, or leave blank for none" },
      { key: "respiratory",     label: "Respiratory", type: "multi",
        options: ["Asthma", "Wheezing", "Persistent Cough"] },
      { key: "neurological",    label: "Neurological", type: "multi",
        options: ["Seizures", "Fainting", "Epilepsy"] },
      { key: "cardiovascular_hx", label: "Cardiovascular", type: "multi",
        options: ["Chest Pain", "Palpitations", "Dizziness"] },
      { key: "allergies",       label: "Allergies", type: "multi",
        options: ["Food", "Drug", "Environment"] },
      { key: "infectious",      label: "Infectious Disease", type: "multi",
        options: ["Malaria", "Dengue", "Typhoid"] },
      { key: "current_meds",    label: "Current Medications", type: "textarea", full: true },
      { key: "immunization_uptodate", label: "Immunization Up-to-Date", type: "select", options: ["Yes", "No"] },
      { key: "vaccinations",    label: "List of Vaccinations & Dates", type: "textarea", full: true },
      { key: "last_deworming",  label: "Last Deworming Date", type: "date" },
    ],
  },
  {
    id: "life",
    section: "Training, Nutrition & Lifestyle",
    fields: [
      { key: "sports_played",      label: "Sports played and at what level", type: "textarea", full: true },
      { key: "sports_experience_yrs", label: "Sports Experience", type: "number", unit: "yrs" },
      { key: "training_hrs_week",  label: "Training Hours / Week", type: "number" },
      { key: "supplements_use",    label: "Supplements", type: "select", options: ["No", "Yes"] },
      { key: "supplements_names",  label: "Supplement names", type: "textarea", full: true },
      { key: "exercise_intolerance", label: "Exercise Intolerance / Early Fatigue", type: "select", options: ["No", "Yes"] },
      { key: "training_pain",      label: "Pain during Training / Ergometer", type: "select", options: ["No", "Yes"] },
      { key: "diet_type",          label: "Diet Type", type: "select", options: ["Veg", "Non-Veg"] },
      { key: "water_intake_l",     label: "Daily Water Intake", type: "number", unit: "L" },
      { key: "sleep_hrs",          label: "Sleep", type: "number", unit: "hrs/night" },
      { key: "meals_access",       label: "Access to 3 Meals", type: "select", options: ["Regular", "Irregular"] },
      { key: "living_env",         label: "Living Environment", type: "select", options: ["Adequate", "Damp / Crowded"] },
      { key: "psychosocial",       label: "Psychosocial Status", type: "multi",
        options: ["High Stress", "Anxiety", "Academic Pressure", "Stable", "Other"] },
      { key: "psychosocial_other", label: "If Other (specify)", type: "text" },
      { key: "family_history_notes", label: "Family Medical History (notes)", type: "textarea", full: true },
      { key: "father_height_cm",   label: "Father's Height", type: "number", unit: "cm" },
      { key: "mother_height_cm",   label: "Mother's Height", type: "number", unit: "cm" },
    ],
  },
  {
    id: "family",
    section: "Family History of Chronic Illness",
    fields: [
      { key: "family_chronic",   label: "Family History", type: "multi",
        options: ["Hypertension", "Diabetes", "Cardiac Death (<50 yrs)", "Tuberculosis", "Mental Health"] },
      { key: "sibling_health",   label: "Sibling Health Issues", type: "textarea", full: true },
    ],
  },
  {
    id: "female",
    section: "Female Athletes Only",
    note: "Fill only if applicable.",
    fields: [
      { key: "menarche_age",        label: "Age at Menarche", type: "number" },
      { key: "menarche_not_achieved", label: "Not yet achieved", type: "checkbox" },
      { key: "irregular_periods",   label: "Irregular Periods", type: "checkbox" },
      { key: "amenorrhea",          label: "Amenorrhea", type: "checkbox" },
      { key: "other_cycle",         label: "Other cycle notes", type: "text" },
      { key: "severe_pain",         label: "Severe Pain / Heavy Bleeding", type: "select", options: ["No", "Yes"] },
      { key: "hygiene_access",      label: "Access to Hygiene Products", type: "select", options: ["Adequate", "Limited"] },
    ],
  },
  {
    id: "clin",
    section: "Clinical Assessment",
    fields: [
      { key: "cardiovascular_exam", label: "Cardiovascular (Murmurs / Rhythm)", type: "textarea", full: true },
      { key: "respiratory_exam",    label: "Respiratory (Breath Sounds)", type: "textarea", full: true },
      { key: "msk_exam",            label: "Musculoskeletal (Spine / Joints / Posture)", type: "textarea", full: true },
      { key: "neuro_exam",          label: "Neurological (Reflexes / Coordination)", type: "textarea", full: true },
      { key: "abdominal_exam",      label: "Abdominal (Tenderness / Organomegaly)", type: "textarea", full: true },
      { key: "vision_hearing",      label: "Vision / Hearing Screen", type: "textarea", full: true },
    ],
  },
  {
    id: "fit",
    section: "Fitness Status & Recommendations",
    fields: [
      { key: "fitness_status", label: "Fitness Status", type: "select",
        options: ["FIT", "TEMPORARILY UNFIT", "UNFIT"] },
      { key: "deferred_for",   label: "If Temporarily Unfit — deferred for", type: "text" },
      { key: "unfit_reason",   label: "If Unfit — not cleared due to", type: "text" },
      { key: "recommendations", label: "Recommended Interventions (Iron, Calcium, Vit D, ...)", type: "textarea", full: true },
      { key: "supplement_brands", label: "Supplement names with brands", type: "textarea", full: true },
      { key: "additional_notes", label: "Additional Medical Information / Relevant Notes", type: "textarea", full: true },
    ],
  },
  {
    id: "att",
    section: "Attestation",
    fields: [
      { key: "medical_officer_name", label: "Medical Officer Name", type: "text" },
      { key: "medical_officer_reg",  label: "Registration Number", type: "text" },
      { key: "attestation_date",     label: "Date", type: "date" },
    ],
  },
];

export function isBaselineEmpty(data) {
  if (!data) return true;
  return Object.values(data).every((v) => {
    if (v == null) return true;
    if (typeof v === "string") return v.trim() === "";
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === "boolean") return v === false;
    return false;
  });
}
